import React from 'react';
import { Box, Container, Typography, Button, Grid, Chip, Stack, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';

const TRUST_POINTS = [
  'Atendimento especializado para PJ',
  'Sem custo para solicitar a análise',
  'Resposta rápida via WhatsApp',
];

const FloatingCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  sx?: object;
  delay?: number;
}> = ({ icon, title, subtitle, sx, delay = 0 }) => (
  <Box
    component={motion.div}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    sx={{
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      bgcolor: 'rgba(255,255,255,0.97)',
      borderRadius: '16px',
      p: 2,
      pr: 3,
      boxShadow: '0 20px 50px rgba(4, 20, 22, 0.35)',
      maxWidth: 260,
      ...sx,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '12px',
        bgcolor: 'rgba(14, 75, 79, 0.08)',
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.3 }}>
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const HeroBanner: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      id="top"
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0A2038 0%, #123A61 55%, #1B4E7D 100%)',
        color: '#fff',
        pt: { xs: 15, md: 17 },
        pb: { xs: 12, md: 16 },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.14) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          opacity: 0.5,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 560,
          height: 560,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(45, 212, 191, 0.28) 0%, transparent 70%)',
          top: -180,
          right: -160,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box component={motion.div} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Chip
                label="PLANOS DE SAÚDE PARA MÉDICOS E CLÍNICAS"
                sx={{
                  bgcolor: 'rgba(45, 212, 191, 0.16)',
                  color: '#5EEAD4',
                  border: '1px solid rgba(45, 212, 191, 0.4)',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  fontSize: '0.7rem',
                  mb: 3,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.4rem' },
                  lineHeight: 1.15,
                  mb: 3,
                }}
              >
                Seu plano de saúde pode ser mais inteligente para o seu CNPJ.
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.15rem' },
                  lineHeight: 1.6,
                  color: 'rgba(255,255,255,0.82)',
                  maxWidth: 560,
                  mb: 4,
                  fontWeight: 400,
                }}
              >
                A MedPrime Benefícios ajuda médicos, clínicas e profissionais da saúde a encontrarem
                opções de planos empresariais com excelente custo-benefício.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                <Button
                  component="a"
                  href="#cotacao"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    bgcolor: 'secondary.main',
                    color: '#0A2038',
                    fontWeight: 700,
                    '&:hover': { bgcolor: 'secondary.light' },
                  }}
                >
                  Quero uma Cotação
                </Button>
                <Button
                  component="a"
                  href="#como-funciona"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.4)',
                    borderWidth: 1.5,
                    color: '#fff',
                    '&:hover': { borderColor: '#fff', borderWidth: 1.5, bgcolor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  Entender Como Funciona
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }}>
                {TRUST_POINTS.map((point) => (
                  <Box key={point} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#5EEAD4' }} />
                    <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)' }}>
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box sx={{ position: 'relative', height: { xs: 240, sm: 320, md: 460 } }}>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: { xs: '20px', md: '28px' },
                  overflow: 'hidden',
                  boxShadow: '0 24px 64px rgba(4, 12, 22, 0.45)',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'url(/images/banners/medprime-hero-doctor.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center 15%',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: {
                      xs: 'linear-gradient(180deg, rgba(10, 32, 56, 0.35) 0%, transparent 30%)',
                      md: 'linear-gradient(100deg, #0A2038 0%, rgba(10, 32, 56, 0.55) 22%, transparent 48%)',
                    },
                  }}
                />
              </Box>
              {isDesktop && (
                <>
                  <FloatingCard
                    icon={<GroupsRoundedIcon />}
                    title="Você e sua equipe"
                    subtitle="Inclua sócios, família e colaboradores"
                    sx={{ top: 24, left: 0 }}
                    delay={0.2}
                  />
                  <FloatingCard
                    icon={<TrendingDownRoundedIcon />}
                    title="Análise personalizada"
                    subtitle="Avaliação de alternativas ao seu reajuste atual"
                    sx={{ bottom: 24, left: 0 }}
                    delay={0.4}
                  />
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroBanner;
